/**
 * Toolbar Slider - Lightweight announcement rotator
 * Desktop "columns" layout uses CSS only (no Swiper) to avoid CLS from
 * stacking → row reflow while Swiper boots.
 */

const DESKTOP_MQ = '(min-width: 1024px)';

class ToolbarSlider {
  constructor(element) {
    this.element = element;
    this.swiper = null;
    this.mediaQuery = window.matchMedia(DESKTOP_MQ);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);

    try {
      this.options = JSON.parse(element.dataset.swiperOptions || '{}');
    } catch (e) {
      console.error('[ToolbarSlider] Failed to parse options:', e);
      this.options = {};
    }

    this.desktopColumnsMode = element.dataset.desktopLayout === 'columns';

    this.prepareSlides();
    this.mediaQuery.addEventListener('change', this.onBreakpointChange);
    this.syncMode();
  }

  prepareSlides() {
    const wrapper = this.element.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    Array.from(wrapper.children).forEach((child) => {
      if (child.tagName === 'STYLE' || child.tagName === 'SCRIPT') return;
      if (!child.classList.contains('swiper-slide')) {
        child.classList.add('swiper-slide');
      }
    });
  }

  shouldUseDesktopStatic() {
    return this.desktopColumnsMode && this.mediaQuery.matches;
  }

  syncMode() {
    if (this.shouldUseDesktopStatic()) {
      this.enableDesktopStatic();
    } else {
      this.enableSlider();
    }
  }

  onBreakpointChange() {
    this.syncMode();
  }

  enableDesktopStatic() {
    this.destroySwiper();
    this.prepareSlides();
    this.element.classList.add('swiper-initialized', 'toolbar-desktop-static');
  }

  enableSlider() {
    this.element.classList.remove('toolbar-desktop-static');
    if (this.swiper) return;
    this.waitForSwiper();
  }

  waitForSwiper() {
    if (this.shouldUseDesktopStatic()) {
      this.enableDesktopStatic();
      return;
    }

    if (typeof Swiper !== 'undefined') {
      this.init();
    } else {
      setTimeout(() => this.waitForSwiper(), 100);
    }
  }

  init() {
    if (this.shouldUseDesktopStatic() || this.swiper) {
      return;
    }

    try {
      this.prepareSlides();

      this.swiper = new Swiper(this.element, {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 600,
        fadeEffect: {
          crossFade: true
        },
        watchSlidesProgress: true,
        ...this.options,
        on: {
          init: () => {
            this.element.classList.add('swiper-initialized');
            this.removeDefaultArrowStyles();
          },
          ...(this.options.on || {})
        }
      });
    } catch (e) {
      console.error('[ToolbarSlider] Init failed:', e);
    }
  }

  removeDefaultArrowStyles() {
    const prev = this.element.querySelector('.swiper-button-prev');
    const next = this.element.querySelector('.swiper-button-next');

    [prev, next].forEach((btn) => {
      if (btn) {
        btn.style.backgroundImage = 'none';
      }
    });
  }

  destroySwiper() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  destroy() {
    this.mediaQuery.removeEventListener('change', this.onBreakpointChange);
    this.destroySwiper();
  }
}

function initToolbarSliders() {
  document.querySelectorAll('[data-toolbar-slider]').forEach((slider) => {
    if (!slider.dataset.toolbarInit) {
      slider.dataset.toolbarInit = 'true';
      new ToolbarSlider(slider);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initToolbarSliders);
} else {
  initToolbarSliders();
}

document.addEventListener('shopify:section:load', () => {
  setTimeout(initToolbarSliders, 100);
});

window.ToolbarSlider = ToolbarSlider;
